import type { SWRResponse } from 'swr';

/**
 * Merges a locally-known placeholder into an SWR response *after* SWR, instead of handing it over
 * as `fallbackData`.
 *
 * Why it must not be `fallbackData`: SWR starts its mount revalidation synchronously only while its
 * own `data` is `undefined`. With `fallbackData` defined it takes the other branch and defers the
 * call through `requestAnimationFrame` (`swr/dist/index/index.mjs`, the `shouldDoInitialRevalidation`
 * block). A document that is not being rendered — a background tab opened with cmd-click, an
 * off-screen app webview, a Playwright or visual-diff page that never becomes active — never gets
 * that frame, so the request is never issued at all: no error, no retry, no timeout (MPH-964).
 * Keeping SWR's own data `undefined` puts it back on the synchronous branch, and the placeholder is
 * merged back in here.
 *
 * Getters, not a plain object: SWR tracks which fields a component actually reads and re-renders it
 * only for those, so a consumer that reads `data` alone has to stay unaffected by `isValidating` and
 * by the `isLoading`/`error` pair. Spreading the response would touch all four and subscribe every
 * consumer to every field.
 *
 * `=== undefined` rather than `??`: a `null` answer from the route is a real value — asked, and
 * there is nothing — and must not fall back to the placeholder.
 */
export function withPlaceholderData<Data>(
  swrResponse: SWRResponse<Data>,
  placeholder: Data | undefined
): SWRResponse<Data> {
  return {
    mutate: swrResponse.mutate,
    get data() {
      const swrData = swrResponse.data;
      return swrData === undefined ? placeholder : swrData;
    },
    get error() {
      return swrResponse.error;
    },
    get isValidating() {
      return swrResponse.isValidating;
    },
    get isLoading() {
      return swrResponse.isLoading;
    },
  };
}
